import type { PlantacionDeInforme } from "@/lib/informes";
import { textoDeNumero } from "@/lib/montos";
import type {
  CampoDeTerreno,
  ItemDeTerreno,
  RegistroDeTerrenoDelInforme,
  SeccionDeTerreno,
} from "@/lib/terreno";

import type { ModoDelTerreno } from "./cuadroDeConstrucciones";
import { claveDeEspecie, nombreDeLaPlantacion, PLANTACION_VACIA, type FormularioDePlantacion } from "./plantaciones";

/* ---------------------------------------------------------------------------
   Lo que la pestaña "Descriptor de plantaciones" lee del registro de terreno:
   las plantaciones que el encargado creó en el predio y los registros que
   cuelgan de cada fila.

   Es lógica PURA, gemela de `maquinariasTerreno.ts`, y vive fuera del
   componente por el mismo motivo: decidir a qué fila pertenece un registro, o
   en qué grupo cae una plantación de terreno, son reglas que pueden estar mal
   sin que nada se vea roto.

   DIFERENCIAS CON MAQUINARIAS: las plantaciones NO tienen "No está en el
   predio" (el diseño no lo dibuja: un cuartel arrancado se explica con un audio
   o un texto), y la tabla está AGRUPADA POR ESPECIE, así que una plantación de
   terreno cae dentro del grupo de su especie y no en un bloque aparte.

   Como las máquinas, las plantaciones se guardan de a UNA (POST/PATCH): una
   plantación de terreno se ADOPTA abriendo el formulario de alta precargado, y
   "Es la misma que" es un PATCH de la fila elegida.
--------------------------------------------------------------------------- */

const PREFIJO_DE_TERRENO = "plantacion-terreno:";

/** Una plantación que el encargado creó en la App y que ninguna fila adoptó todavía. */
export interface ItemDePlantacionPorEnlazar {
  uuid: string;
  item: ItemDeTerreno;
}

/** Todo lo que la lista de plantaciones necesita saber del terreno. */
export interface TerrenoDeLaLista {
  informeId: number;
  modo: ModoDelTerreno;
  /** Quien fue a terreno: sus iniciales encabezan el registro. */
  encargado: string | null;
  items: ItemDeTerreno[];
  porEnlazar: ItemDePlantacionPorEnlazar[];
  onAbrirFoto: (uuid: string) => void;
  /** "Es la misma que": enlaza el registro de terreno a una fila ya guardada. */
  onVincular: (uuid: string, plantacionId: number) => void;
}

/** La sección de plantaciones del registro de terreno, o `null`. */
export function seccionDePlantaciones(
  registro: RegistroDeTerrenoDelInforme | undefined,
): SeccionDeTerreno | null {
  return registro?.secciones.find((s) => s.clave === "plantaciones") ?? null;
}

/**
 * Las plantaciones de terreno que todavía no tienen fila en el cuadro (el
 * servidor cuelga las ya enlazadas de `plantacion:<id>` y deja de listarlas
 * aparte).
 *
 * TAMBIÉN SE DESCARTA LO QUE UNA FILA GUARDADA YA ENLAZA: entre el alta con
 * `terreno_ref` y la relectura del registro hay un instante en que el informe
 * ya tiene la fila y el registro todavía lista la plantación suelta, y se vería
 * dos veces.
 */
export function itemsPorEnlazarDePlantaciones(
  registro: RegistroDeTerrenoDelInforme | undefined,
  guardadas: Pick<PlantacionDeInforme, "terreno_ref">[] = [],
): ItemDePlantacionPorEnlazar[] {
  const enlazadas = new Set(guardadas.flatMap((p) => (p.terreno_ref ? [p.terreno_ref] : [])));

  return (seccionDePlantaciones(registro)?.items ?? []).flatMap((item) => {
    if (!item.nuevo || !item.referencia.startsWith(PREFIJO_DE_TERRENO)) return [];

    const uuid = item.referencia.slice(PREFIJO_DE_TERRENO.length);

    return enlazadas.has(uuid) ? [] : [{ uuid, item }];
  });
}

/** "2 plantaciones creadas en terreno": el título del aviso, concordado. */
export function tituloDelAviso(cuantas: number): string {
  return cuantas === 1
    ? "1 plantación creada en terreno"
    : `${cuantas} plantaciones creadas en terreno`;
}

/**
 * ¿La plantación nació en la App y todavía no está completa para valorizarse?
 *
 * Una plantación NO lleva valores en UF (es un descriptor), así que «sin
 * valorizar» se decide por lo que el método de activos biológicos va a
 * necesitar: la superficie y la densidad. Sin NINGUNA de las dos la fila solo
 * trae la identidad (especie, variedad, año…); con una sola ya hay por dónde
 * seguir. `null` es una fila TERRENO sin guardar.
 */
export function creadaEnTerrenoSinValorizar(
  fila: Pick<PlantacionDeInforme, "terreno_ref" | "superficie_ha" | "plantas_por_ha"> | null,
): boolean {
  if (fila === null) return true;

  return fila.terreno_ref !== null && fila.superficie_ha === null && fila.plantas_por_ha === null;
}

/** La clave de la fila de una plantación de terreno (expansión del registro). */
export function claveDePlantacionDeTerreno(uuid: string): string {
  return `terreno-${uuid}`;
}

/** La clave de la fila de una plantación guardada. */
export function claveDePlantacion(id: number): string {
  return `plantacion-${id}`;
}

/** La especie con la que una plantación de terreno se agrupa: la suya, o el nombre del ítem. */
function especieDelItem(item: ItemDeTerreno): string {
  return item.especie?.trim() || item.nombre.trim();
}

/** Cómo se llama una plantación de terreno en una frase ("Cerezos Lapins"). */
export function nombreDelItem(item: ItemDeTerreno): string {
  const variedad = item.variedad?.trim();

  return variedad ? `${especieDelItem(item)} ${variedad}` : especieDelItem(item);
}

/**
 * El formulario de alta precargado con lo que la App ya sabe de la plantación.
 *
 * LAS PLANTAS TOTALES NO SE CALCULAN ACÁ: las calcula el servidor al guardar
 * (superficie × densidad), y el formulario solo muestra su vista previa.
 * La superficie llega como número y se escribe con coma, como el resto del
 * formulario: con el punto crudo, guardar sin tocar nada la leería como miles.
 */
export function formularioDesdeElItem(item: ItemDeTerreno): FormularioDePlantacion {
  return {
    ...PLANTACION_VACIA,
    especie: especieDelItem(item),
    variedad: item.variedad ?? "",
    anio_plantacion: textoDeNumero(item.anio_plantacion ?? null),
    superficie_ha: item.superficie_ha == null ? "" : String(item.superficie_ha).replace(".", ","),
    cuartel: item.cuartel ?? "",
    portainjerto: item.portainjerto ?? "",
    distancia_plantacion: item.distancia_plantacion ?? "",
    plantas_por_ha: textoDeNumero(item.plantas_por_ha ?? null),
    sistema_riego: item.sistema_riego ?? "",
  };
}

/**
 * El ítem de terreno cuyos registros se muestran bajo una fila guardada: el
 * servidor ya colgó ahí lo enlazado (`plantacion:<id>`).
 */
export function itemDeLaPlantacion(
  items: ItemDeTerreno[],
  plantacion: Pick<PlantacionDeInforme, "id">,
): ItemDeTerreno | null {
  return items.find((i) => i.referencia === `plantacion:${plantacion.id}`) ?? null;
}

export interface OpcionParaVincular {
  clave: string;
  etiqueta: string;
}

/**
 * Las filas con las que una plantación de terreno puede decir "es la misma
 * que": las ya guardadas que todavía no tienen un registro enlazado (el
 * servidor exige que cada uuid se use una sola vez por informe).
 *
 * LA ETIQUETA LLEVA EL CUARTEL: varias filas comparten especie y variedad, y
 * sin el cuartel el selector ofrecería tres opciones idénticas.
 */
export function opcionesParaVincular(
  plantaciones: Pick<PlantacionDeInforme, "id" | "especie" | "variedad" | "cuartel" | "terreno_ref">[],
): OpcionParaVincular[] {
  return plantaciones
    .filter((p) => p.terreno_ref === null)
    .map((p) => ({
      clave: String(p.id),
      etiqueta: p.cuartel?.trim()
        ? `${nombreDeLaPlantacion(p)} · cuartel ${p.cuartel.trim()}`
        : nombreDeLaPlantacion(p),
    }));
}

/* --- El lugar de las filas TERRENO en la tabla agrupada ------------------- */

/** Las plantaciones de terreno que caen en el grupo de la especie con esa clave. */
export function porEnlazarDeLaEspecie(
  porEnlazar: ItemDePlantacionPorEnlazar[],
  clave: string,
): ItemDePlantacionPorEnlazar[] {
  return porEnlazar.filter((p) => claveDeEspecie(especieDelItem(p.item)) === clave);
}

/** Un grupo que solo tiene filas de terreno: su especie no tiene ninguna fila visible. */
export interface GrupoDeTerrenoSuelto {
  clave: string;
  /** Tal como lo escribió la PRIMERA plantación de terreno del grupo. */
  especie: string;
  porEnlazar: ItemDePlantacionPorEnlazar[];
}

/**
 * Los grupos para las plantaciones de terreno cuya especie NO tiene grupo
 * visible (ninguna fila guardada, o el filtro las escondió todas).
 *
 * MISMAS REGLAS QUE `agruparPorEspecie`: clave normalizada, nombre de la
 * primera que lo estrena, orden de primera aparición.
 */
export function gruposDeTerrenoSueltos(
  porEnlazar: ItemDePlantacionPorEnlazar[],
  clavesVisibles: string[],
): GrupoDeTerrenoSuelto[] {
  const visibles = new Set(clavesVisibles);
  const grupos = new Map<string, GrupoDeTerrenoSuelto>();

  for (const p of porEnlazar) {
    const especie = especieDelItem(p.item);
    const clave = claveDeEspecie(especie);

    if (visibles.has(clave)) continue;

    const grupo = grupos.get(clave);

    if (grupo === undefined) grupos.set(clave, { clave, especie, porEnlazar: [p] });
    else grupo.porEnlazar.push(p);
  }

  return [...grupos.values()];
}

/** Lo que el encargado dijo de la descripción general de las plantaciones, o `null`. */
export function campoDeLaDescripcionDePlantaciones(
  registro: RegistroDeTerrenoDelInforme | undefined,
): CampoDeTerreno | null {
  return registro?.secciones.find((s) => s.clave === "plantaciones")?.campos.find((c) => c.clave === "plantaciones") ?? null;
}
